/**
 * 陸上勤務者向けのモバイルポータルホーム（/portal-home）
 * 方針: 使いやすさ最優先。白基調・濃い文字・大きなボタンと文字・迷わない並び。
 * 上から「あいさつ → 出退勤 → 確認してください（未読の要点）→ 大分類 → お知らせ・回覧」。
 * 陸上管理者はログイン後ここに着地し、下部ナビもポータル用に切り替わる。
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNav } from '../../components/BottomNav';
import { PIcon, PIconChip } from '../../components/PIcon';
import { PushBell } from '../../components/PushBell';
import { apiFetch } from '../../services/api';
import { Avatar, PORTAL_ACCENT, PORTAL_ACCENT_DARK, PORTAL_ACCENT_SOFT, fmtDateTime, EmptyState } from './ui';

interface FeedPost {
  id: string; post_type: string; title: string; author_name: string | null; created_at: string;
  deadline: string | null; excerpt?: string | null; attachment_count?: number; my_read: boolean;
}
interface Summary {
  board_unread: number; chat_unread: number; manuals_unread: number;
  my_expenses_active: number; my_requests_pending: number;
  approvals_pending: number; is_admin: boolean;
  office_today: { clock_in: string | null; clock_out: string | null } | null;
  recent_posts: FeedPost[];
}

const card: React.CSSProperties = {
  background: '#fff', borderRadius: 12, border: '1px solid #e3e6ea', padding: '14px 14px',
};

export default function PortalMobileHomePage() {
  const nav = useNavigate();
  const [sum, setSum] = useState<Summary | null>(null);
  const [msg, setMsg] = useState('');
  const user = (() => { try { return JSON.parse(localStorage.getItem('catms_user') ?? '{}'); } catch { return {}; } })();

  const load = useCallback(() => {
    apiFetch<{ data: Summary }>('/portal/summary', {}, { noRedirect: true })
      .then(r => setSum(r.data)).catch(() => { /* 圏外時はSWキャッシュ or 非表示 */ });
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const punch = async (type: 'in' | 'out') => {
    setMsg('');
    try {
      const r = await apiFetch<{ data: { clock_in: string | null; clock_out: string | null } }>('/portal/office/punch', { method: 'POST', body: JSON.stringify({ type }) });
      setMsg(type === 'in' ? `出勤 ${r.data.clock_in} を打刻しました` : `退勤 ${r.data.clock_out} を打刻しました`);
      load();
    } catch (e) { setMsg(e instanceof Error ? e.message : '打刻に失敗しました'); }
  };

  const today = sum?.office_today;
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 11 ? 'おはようございます' : hour < 18 ? 'こんにちは' : 'おつかれさまです';
  const dateLabel = now.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric', weekday: 'short' });

  // 確認してください（0件の項目は出さず、見るべきものだけ並べる）
  const todos: Array<{ label: string; count: number; path: string }> = [
    { label: '未読の連絡', count: sum?.board_unread ?? 0, path: '/portal-board' },
    { label: 'チャットの未読', count: sum?.chat_unread ?? 0, path: '/portal-chat' },
    { label: '要確認のマニュアル', count: sum?.manuals_unread ?? 0, path: '/portal-manuals' },
    ...(sum?.is_admin
      ? [{ label: '承認待ち（経費・申請）', count: sum?.approvals_pending ?? 0, path: '/portal-requests' }]
      : [{ label: '自分の申請（進行中）', count: (sum?.my_requests_pending ?? 0) + (sum?.my_expenses_active ?? 0), path: '/portal-requests' }]),
  ].filter(t => t.count > 0);

  const tiles: Array<[string, string, string]> = [
    ['megaphone', '連絡・掲示板', '/portal-board'],
    ['chat', 'チャット', '/portal-chat'],
    ['book', 'マニュアル', '/portal-manuals'],
    ['receipt', '経費', '/portal-expenses'],
    ['filecheck', '各種申請', '/portal-requests'],
    ['calendar', '勤怠表', '/portal-office'],
    ['user', 'マイページ', '/portal-profile'],
  ];

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: '#f6f7f8' }}>
      {/* ヘッダー（白・ノッチ対応） */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e3e6ea', flexShrink: 0 }}>
        <div className="app-safe-band" aria-hidden="true" />
        <div style={{ padding: '4px 16px 12px', display: 'flex', alignItems: 'center', gap: 11 }}>
          <Avatar name={user?.user_name} size={42} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, color: '#50545b', fontWeight: 600 }}>{dateLabel}・{greeting}</div>
            <div style={{ fontSize: 19, fontWeight: 800, color: '#1a1c1f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.user_name ?? ''} さん
            </div>
          </div>
          <PushBell />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 90px' }}>
        {/* 出退勤（最上段・大きなボタン） */}
        <div style={{ ...card, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 14.5, fontWeight: 800, color: '#1a1c1f' }}>出退勤の打刻</span>
            <button onClick={() => nav('/portal-office')}
              style={{ marginLeft: 'auto', border: '1px solid #c8cdd3', background: '#fff', borderRadius: 8, padding: '7px 12px', color: '#3d4248', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              勤怠表を見る
            </button>
          </div>
          <div style={{ fontSize: 14, color: '#3d4248', marginBottom: 12, fontWeight: 600 }}>
            {msg || (today?.clock_in
              ? <>出勤 <span style={{ color: PORTAL_ACCENT_DARK, fontWeight: 800 }}>{today.clock_in}</span>{today.clock_out ? <>　退勤 <span style={{ color: PORTAL_ACCENT_DARK, fontWeight: 800 }}>{today.clock_out}</span></> : '　（勤務中）'}</>
              : '本日の打刻はまだです')}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button disabled={!!today?.clock_in} onClick={() => void punch('in')}
              style={{
                flex: 1, padding: '17px 0', borderRadius: 12, border: 'none', fontSize: 17, fontWeight: 900,
                cursor: today?.clock_in ? 'not-allowed' : 'pointer',
                background: today?.clock_in ? '#e3e6ea' : PORTAL_ACCENT,
                color: today?.clock_in ? '#8b9096' : '#fff',
              }}>出 勤</button>
            <button disabled={!today?.clock_in || !!today?.clock_out} onClick={() => void punch('out')}
              style={{
                flex: 1, padding: '17px 0', borderRadius: 12, fontSize: 17, fontWeight: 900,
                cursor: (!today?.clock_in || today?.clock_out) ? 'not-allowed' : 'pointer',
                background: '#fff',
                border: `2px solid ${(!today?.clock_in || today?.clock_out) ? '#e3e6ea' : PORTAL_ACCENT}`,
                color: (!today?.clock_in || today?.clock_out) ? '#8b9096' : PORTAL_ACCENT_DARK,
              }}>退 勤</button>
          </div>
        </div>

        {/* 確認してください（あるときだけ） */}
        {todos.length > 0 && (
          <div style={{ ...card, marginBottom: 12, borderColor: '#f0c36d', background: '#fffdf5' }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: '#6b4e00', marginBottom: 8 }}>確認してください</div>
            {todos.map(t => (
              <button key={t.label} onClick={() => nav(t.path)}
                style={{
                  display: 'flex', alignItems: 'center', width: '100%', gap: 8, padding: '12px 12px',
                  border: '1px solid #eadfbf', borderRadius: 10, background: '#fff', cursor: 'pointer', marginBottom: 6, textAlign: 'left',
                }}>
                <span style={{ fontSize: 14.5, fontWeight: 700, color: '#1a1c1f', flex: 1 }}>{t.label}</span>
                <span style={{ background: '#d93025', color: '#fff', borderRadius: 999, fontSize: 13, fontWeight: 800, padding: '3px 11px' }}>{t.count}件</span>
                <span style={{ color: '#8b9096', fontSize: 16 }}>›</span>
              </button>
            ))}
          </div>
        )}

        {/* 大分類（2列・大きめ） */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          {tiles.map(([icon, label, path]) => (
            <button key={path} onClick={() => nav(path)}
              style={{
                ...card, padding: '15px 8px', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minHeight: 74,
              }}>
              <PIconChip name={icon} size={42} />
              <span style={{ fontSize: 14, fontWeight: 800, color: '#1a1c1f' }}>{label}</span>
            </button>
          ))}
        </div>

        {/* お知らせ・回覧 */}
        <div style={{ display: 'flex', alignItems: 'center', margin: '0 2px 8px' }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: '#1a1c1f' }}>お知らせ・回覧</span>
          <button onClick={() => nav('/portal-board')}
            style={{ marginLeft: 'auto', border: 'none', background: 'none', color: PORTAL_ACCENT_DARK, fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
            すべて見る ›
          </button>
        </div>
        {!sum || sum.recent_posts.length === 0 ? (
          <div style={{ ...card, padding: 8 }}><EmptyState img="/portal/empty-board.png" text="投稿はまだありません" /></div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {sum.recent_posts.slice(0, 5).map(p => (
              <button key={p.id} onClick={() => nav('/portal-board')}
                style={{ ...card, textAlign: 'left', cursor: 'pointer', padding: '13px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 6, flexShrink: 0,
                    background: p.post_type === 'circular' ? '#fff1c2' : PORTAL_ACCENT_SOFT,
                    color: p.post_type === 'circular' ? '#6b4e00' : PORTAL_ACCENT_DARK,
                  }}>{p.post_type === 'circular' ? '回覧' : 'お知らせ'}</span>
                  <span style={{ fontSize: 11.5, color: '#50545b' }}>{p.author_name}・{fmtDateTime(p.created_at)}</span>
                  {!p.my_read && <span style={{ marginLeft: 'auto', background: '#d93025', color: '#fff', borderRadius: 999, fontSize: 10.5, fontWeight: 800, padding: '2px 9px', flexShrink: 0 }}>未読</span>}
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#1a1c1f', margin: '8px 0 2px', lineHeight: 1.45 }}>{p.title}</div>
                {p.excerpt && (
                  <div style={{ fontSize: 13, color: '#3d4248', lineHeight: 1.65, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {p.excerpt}
                  </div>
                )}
                {(p.attachment_count ?? 0) > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 7, fontSize: 12, fontWeight: 700, color: PORTAL_ACCENT_DARK, background: PORTAL_ACCENT_SOFT, borderRadius: 6, padding: '3px 9px' }}>
                    <PIcon name="paperclip" size={11} /> 資料 {p.attachment_count}件
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
